// Binary Maniswap pricing math — TypeScript mirror of the Supabase RPC.
//
// The Arena's binary engine (I-074, DECISIONS.md 2026-06-15) is a
// parameterized constant-product AMM (Maniswap): invariant
// yes^p · no^(1-p) = k, with P(YES) = (p·no)/(p·no + (1-p)·yes). This
// module mirrors the PL/pgSQL helpers binary_prob_yes / binary_quote in
// hub-ui/supabase/migrations/20260615000000_binary_engine_arena_port.sql
// (itself a faithful port of the verified engine/liquidity_binary.py).
//
// The trade ticket imports from here for its pre-flight price-impact
// preview; the canonical answer at trade time still comes from the
// buy_binary RPC.
//
// Drift discipline (mirrors parimutuelPricing.ts):
//   * Single source of truth is the Supabase RPC (binary_quote).
//   * hub-ui/scripts/test_binary_pricing.mjs gates the TS math against
//     the verified engine reference numbers (and, once P3a is applied
//     to the Arena Supabase, against the live RPC — see that script's
//     TODO). The math below MUST stay in lockstep with that script.
//
// Precision: JS number is float64 — identical IEEE-754 ops to the
// Python engine (also float64), so the reference numbers match exactly;
// Postgres NUMERIC is arbitrary precision but agrees within the
// rounding tolerance the drift gate uses.

export type Side = 'YES' | 'NO';

export const MAKER_RAKE = 0.01; // 1% rake on matched volume (DECISIONS.md 2026-06-15)

/** Market-implied P(YES) from the parameterized CPMM reserves. */
export function binaryProbYes(yesR: number, noR: number, p: number): number {
  return (p * noR) / (p * noR + (1 - p) * yesR);
}

export interface Quote {
  shares: number;
  newYes: number;
  newNo: number;
}

/** Mint-and-swap on the parameterized invariant. `net` is stake net of
 *  rake. Returns shares received + post-trade reserves. */
export function quoteBuy(yesR: number, noR: number, p: number, side: Side, net: number): Quote {
  const k = Math.pow(yesR, p) * Math.pow(noR, 1 - p);
  if (side === 'YES') {
    const newNo = noR + net;
    const newYes = Math.pow(k / Math.pow(newNo, 1 - p), 1 / p);
    return { shares: net + (yesR - newYes), newYes, newNo };
  }
  const newYes = yesR + net;
  const newNo = Math.pow(k / Math.pow(newYes, p), 1 / (1 - p));
  return { shares: net + (noR - newNo), newYes, newNo };
}

export interface BuyPreview {
  shares: number;
  rake: number;
  netStake: number;
  probBefore: number;
  probAfter: number;
  priceImpactPp: number;   // signed percentage points of P(YES) movement
  effectivePrice: number;  // gross stake per share
}

/** Pre-flight preview for the trade ticket: takes the GROSS stake,
 *  applies the 1% maker rake, runs the curve on the net, and reports the
 *  price impact. Mirrors the buy_binary RPC. */
export function previewBuy(
  yesR: number, noR: number, p: number, side: Side, stake: number,
): BuyPreview {
  const rake = Math.round(stake * MAKER_RAKE * 1e8) / 1e8;
  const netStake = stake - rake;
  const probBefore = binaryProbYes(yesR, noR, p);
  const { shares, newYes, newNo } = quoteBuy(yesR, noR, p, side, netStake);
  const probAfter = binaryProbYes(newYes, newNo, p);
  return {
    shares,
    rake,
    netStake,
    probBefore,
    probAfter,
    priceImpactPp: (probAfter - probBefore) * 100,
    effectivePrice: stake / shares,
  };
}
